import { Controller } from "@hotwired/stimulus"

// One row of the catalog picker. Owns the expand/collapse of the Baustein's positions
// (lazily loaded into a turbo frame) and the per-position selection that decides what an
// insert actually copies.
//
// The selection is published twice, because a Baustein can be inserted two ways:
//   * button  — injected into the row's form as catalog_item_ids[] right before submit
//   * drag    — mirrored onto the row element's dataset, which catalog_insert.js reads
//               off the SortableJS clone
export default class extends Controller {
  static targets = ["body", "chevron", "itemCheckbox", "wrapToggle", "selectionSummary", "summary"]

  connect() {
    this.syncDataset()
  }

  disconnect() {
    clearTimeout(this.syncTimer)
  }

  // The positions arrive later, through the lazy turbo frame, and each checkbox connects
  // separately — coalesce into one pass once the whole list has landed.
  itemCheckboxTargetConnected() {
    clearTimeout(this.syncTimer)
    this.syncTimer = setTimeout(() => {
      this.syncGroupStates()
      this.renderSummary()
      this.syncDataset()
    }, 0)
  }

  toggle(event) {
    const expanded = this.bodyTarget.classList.toggle("hidden") === false
    this.chevronTarget.classList.toggle("rotate-90", expanded)
    event.currentTarget.setAttribute("aria-expanded", String(expanded))
  }

  // Ticks everything, or clears it when everything is already ticked.
  toggleAll() {
    const boxes = this.itemCheckboxTargets
    const allChecked = boxes.every((box) => box.checked)
    boxes.forEach((box) => (box.checked = !allChecked))
    this.selectionChanged()
  }

  // A group carries its subtree: ticking it ticks everything below, unticking clears it.
  selectionChanged(event) {
    const box = event?.target
    if (box?.dataset.kind === "group") this.cascade(box)
    this.syncGroupStates()
    this.renderSummary()
    this.syncDataset()
  }

  cascade(groupBox) {
    const descendants = this.descendantsOf(groupBox.dataset.itemId)
    descendants.forEach((child) => (child.checked = groupBox.checked))
  }

  descendantsOf(itemId) {
    const direct = this.itemCheckboxTargets.filter((box) => box.dataset.parentId === itemId)
    return direct.flatMap((box) => [box, ...this.descendantsOf(box.dataset.itemId)])
  }

  // A group with a partial selection below it reads as indeterminate rather than
  // silently claiming its whole subtree is in.
  syncGroupStates() {
    this.itemCheckboxTargets
      .filter((box) => box.dataset.kind === "group")
      .forEach((box) => {
        const children = this.descendantsOf(box.dataset.itemId)
        if (children.length === 0) return
        const checked = children.filter((child) => child.checked).length
        box.checked = checked === children.length
        box.indeterminate = checked > 0 && checked < children.length
      })
  }

  // "3 / 5 Positionen · 240,00 €" below the list, and a shortened "3 / 5 Positionen" in
  // the collapsed row header so a partial selection is visible without expanding again.
  renderSummary() {
    if (!this.hasSelectionSummaryTarget) return

    const positions = this.positionCheckboxes()
    const selected = positions.filter((box) => box.checked)
    const total = selected.reduce((sum, box) => sum + Number(box.dataset.lineNet || 0), 0)
    const label = this.selectionSummaryTarget.dataset.unitLabel || ""
    const counted = `${selected.length} / ${positions.length} ${label}`.trim()

    this.selectionSummaryTarget.textContent = `${counted} · ${this.formatMoney(total)}`
    if (this.hasSummaryTarget) {
      this.summaryTarget.dataset.original ||= this.summaryTarget.textContent
      this.summaryTarget.textContent =
        selected.length === positions.length ? this.summaryTarget.dataset.original : counted
    }
  }

  // Locale-formatted amount plus the currency unit Rails rendered into the markup, so
  // this does not hardcode a currency the tenant may not use.
  formatMoney(value) {
    const unit = this.selectionSummaryTarget.dataset.currencyUnit || ""
    const amount = value.toLocaleString(document.documentElement.lang || "de", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    return `${amount} ${unit}`.trim()
  }

  positionCheckboxes() {
    return this.itemCheckboxTargets.filter((box) => box.dataset.kind !== "group")
  }

  // Mirror the selection onto the row so a dragged clone carries it too. A row with
  // nothing loaded (never expanded) publishes no ids, which means "the whole Baustein".
  //
  // An *emptied* selection is a different thing entirely and cannot be expressed by the
  // id list: an empty catalog_item_ids also means "the whole Baustein" server-side. It
  // gets its own flag so the drop handler can refuse the insert the way injectSelection
  // does for the button — otherwise unticking everything and dragging would copy all of
  // it, the exact opposite of what the row shows.
  syncDataset() {
    if (!this.hasItemCheckboxTarget) return

    const ids = this.selectedIds()
    const complete = ids.length === this.itemCheckboxTargets.length
    if (complete && this.wrapInGroup()) {
      delete this.element.dataset.catalogItemIds
    } else {
      this.element.dataset.catalogItemIds = ids.join(",")
    }
    this.element.dataset.catalogWrapGroup = String(this.wrapInGroup())
    this.element.dataset.catalogSelectionEmpty = String(ids.length === 0)
  }

  wrapInGroup() {
    return this.hasWrapToggleTarget ? this.wrapToggleTarget.checked : true
  }

  selectedIds() {
    return this.itemCheckboxTargets.filter((box) => box.checked).map((box) => box.dataset.itemId)
  }

  // Nothing ticked means the insert would be a no-op — block it rather than letting the
  // server fall back to "no selection = everything".
  injectSelection(event) {
    if (!this.hasItemCheckboxTarget) return

    const ids = this.selectedIds()
    if (ids.length === 0) {
      event.preventDefault()
      return
    }

    const form = event.target
    form.querySelectorAll("[data-catalog-selection]").forEach((el) => el.remove())
    ids.forEach((id) => form.appendChild(this.hiddenField("catalog_item_ids[]", id)))
    form.appendChild(this.hiddenField("wrap_in_group", String(this.wrapInGroup())))
  }

  hiddenField(name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    input.dataset.catalogSelection = "true"
    return input
  }
}
